import { Component, EventEmitter, Output, Inject, Renderer2 } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InfoService } from '@app/core/info/info.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject, takeUntil, forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import { environment } from '@env/environment';
import { BusinessService } from '../business.service';
import { SigningService } from '@app/certificates/signing/signing.service';
import { DocumentStatusMap, DocumentStatus, ParcelType } from '../business-models';
import { SessionStorageService } from '@app/core/local-storage/local-storage.service';
import { selectorAuth } from '@app/core';
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { BusinessDownloadService } from '../business-download.service';

@Component({
  selector: 'cloud-history-details',
  templateUrl: './history-details.component.html',
  styleUrls: ['./history-details.component.scss'],
})
export class HistoryDetailsComponent {

  @Output() close: EventEmitter<any> = new EventEmitter();
  wait: boolean;
  pack: any;
  data: any;
  sequences: any;
  readonly dateFormat = environment.dateFormat;
  readonly timeFormat = environment.timeFormat;
  readonly dateTimeFormat = environment.dateTimeFormat;
  signedfileName: any;
  unsubscribe$: Subject<void> = new Subject<void>();
  isRegisteredMail = false;
  recipientNamesCC: { bg: Observable<string>; en: Observable<string> } = { bg: of(''), en: of('') };

  validity = 10080;
  names = [];
  currentCmrId;

  periodsDictionary = [
    { key: '1', unit: 'hour', value: 60 },
    { key: '2', unit: 'hour', value: 120 },
    { key: '4', unit: 'hour', value: 240 },
    { key: '8', unit: 'hour', value: 480 },
    { key: '1', unit: 'day', value: 1440 },
    { key: '2', unit: 'day', value: 2880 },
    { key: '3', unit: 'day', value: 4320 },
    { key: '4', unit: 'day', value: 5760 },
    { key: '5', unit: 'day', value: 7200 },
    { key: '6', unit: 'day', value: 8640 },
    { key: '7', unit: 'day', value: 10080 }
  ];

   displayedColumns = [
    'status',
    'filename',
    'signable',
    'sentTimestamp',
    'deadlineTimestamp',
    'signedTimestamp',
    'actions'
  ];

 displayedColumnsRegisteredMail = [
    'filename',
    'signable',
    'sentTimestamp',
    /*not needed for now
    // 'deadlineTimestamp',
    // 'signedTimestamp',
    // 'actions'
    */
  ];


  constructor(
    private businessService: BusinessService,
    public info: InfoService,
    private translateService: TranslateService,
    private signingService: SigningService,
    private sessionStorageService: SessionStorageService,
    private store: Store<any>,
    @Inject(MAT_DIALOG_DATA) data: number,
    private businessDownloadService: BusinessDownloadService,
    private renderer: Renderer2
  ) {
    this.data = data;
  }

  ngOnInit() {
    this.wait = true;
    this.loadRequestedHistoryDetails();

    this.store
    .select(selectorAuth)
    .subscribe(auth => {
      if (auth != null) {
        this.currentCmrId = auth.userData.cmrId;
      }
    });
  }

  loadRequestedHistoryDetails() {
    this.businessService.getRequestsHistoryDetails(this.data.requestedId.toString())
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.setRequestedHistoryDetails(res.data);
            this.fetchRecipientNames(res.data.sequenceDetails.map((d) => d.cmrId));
            if (this.data.parcelType === ParcelType.BATCH_SIGNING) {
              this.fetchRecipientNamesCC(res.data.ccCmrId);
            }
          } else {
            this.info.error(this.translateService.instant('businessTexts.noDetailsFound'));
            this.closeDialog();
          }
        },
        error: (err) => this.info.error(err.message)
      });
  }

  fetchRecipientNames(ids: string[]) {
    this.businessService.getRecipientsNames(ids).subscribe({
      next: (res) => {
        this.names = res.data;
        this.wait = false;
      },
      error: (err) => this.info.error(err.message)
    });
  }

/**
 * Fetches recipient names based on the provided IDs and updates the local variables
 * `recipientNamesCC.bg` and `recipientNamesCC.en` with the corresponding names in Bulgarian and English.
 *
 * If the recipient names are successfully retrieved, they are joined into a comma-separated string
 * and assigned to the respective language properties. If no names are found, a fallback translation
 * for "no recipients found" is used. In case of an error during the fetch operation, an error message
 * is logged, and fallback translations for "loading recipients error" are assigned.
 *
 * @param ids - An array of recipient IDs for which the names need to be fetched.
 */
fetchRecipientNamesCC(ids: string[]) {
  this.businessService.getRecipientsNames(ids).subscribe({
    next: (res) => {

      const recipientData = res.data as { id: number, nameBg: string; nameEn: string }[];
      const recipientDataBg = recipientData.map(({ nameBg }) => nameBg).join(', ');
      const recipientDataEn = recipientData.map(({ nameEn }) => nameEn).join(', ');


      // Update the local variable
      this.recipientNamesCC.bg = recipientDataBg ? of(recipientDataBg) : this.translateService.get('businessTexts.noRecipientsFound', { lang: 'bg' });
      this.recipientNamesCC.en = recipientDataEn ? of(recipientDataEn) : this.translateService.get('businessTexts.noRecipientsFound', { lang: 'en' });
    },
    error: (err) => {
      this.info.error(err.message);

      // Set fallback values in case of an error
      this.recipientNamesCC.bg = this.translateService.get('businessTexts.loadingRecipientsError', { lang: 'bg' });
      this.recipientNamesCC.en = this.translateService.get('businessTexts.loadingRecipientsError', { lang: 'en' });
    }
  })
}

  closeDialog() {
    this.close.emit(null);
  }

  setRequestedHistoryDetails(content) {
    this.validity = (content.validityTs - content.ts) / 60000;
    this.pack = content;
    this.sequences = this.pack.sequenceDetails;
    if (this.data.typeId) {
      if(this.data.typeId == 240) { //registered mail
        this.setViewedStatus();
        this.isRegisteredMail = true;
        this.displayedColumns = this.displayedColumnsRegisteredMail;
      }
    }
    this.sequences.sort(function (a, b) {
      return a.sequenceIndex
        - b.sequenceIndex;
    });
  }

  setViewedStatus(){
    this.businessService.setViewed(this.data.requestedId.toString()).subscribe(res => {
      if (res) {
        console.log('status changed to viewed', res)
      }
    });
  }

  getTimeAbbr() {
    return this.translateService.currentLang == 'bg' ? 'г. ' : '';
  }

  getSenderName() {
    return this.translateService.currentLang == 'bg' ? this.pack.senderBg : this.pack.senderEn;
  }

  getUnitTranslation(p, unit) {
    return this.translateService.instant(p == 1 ? 'settingsList.' + unit : 'settingsList.' + unit + 's');
  }

  isDownloadable(docStatus) {

    const packDownloadableStatus = DocumentStatusMap.get(DocumentStatus[this.pack.packageStatus]).key;
    const docDownloadableStatus = DocumentStatusMap.get(DocumentStatus[docStatus]).key;
    const archiveStatus = this.data.archiveStatus;

    if(archiveStatus === 1){
      return false;
    }

    if (packDownloadableStatus != 'ERROR' && packDownloadableStatus != 'REMOVED' && packDownloadableStatus != 'DELETED') {
      return true;
    }else{
      if(docDownloadableStatus === 'SIGNED'  || docDownloadableStatus == 'ARCHIVED' || docDownloadableStatus == 'RECEIVED'){
        return true;
      }
      return false;
    }
  }

  isMine(cmrId) {
    if(this.data.typeId == 230){ //if package and details show actions for only mine
      return cmrId == this.currentCmrId;
    } else{
      return false;
    }
  }

  onDownloadDocument(doc) {
    this.onDownloadFile(doc.usageId);
  }

  onDownloadSimpleRecord(rec) {
    console.log(rec.usageId, 'SIMPLE');
    this.onDownloadReport(rec.usageId, 'SIMPLE')
  }

  onDownloadDetailedRecord(rec) {
    console.log(rec, 'DETAILED');
    this.onDownloadReport(rec.usageId, 'DETAILED')
  }

  getIconName(status) {
    return DocumentStatusMap.get(DocumentStatus[status]).icon;
  }

  getIconClass(status) {
    return DocumentStatusMap.get(DocumentStatus[status]).colorClass;
  }

  getIconTooltip(file) {
    if (!file.signable) {
      return 'historyTexts.documentStatusSent';
    } else
      return DocumentStatusMap.get(DocumentStatus[file.status]).tooltip;
  }

  getPackageIconName() {
    return DocumentStatusMap.get(DocumentStatus[this.data.status]).icon;
  }

  getpackageIconClass() {
    return DocumentStatusMap.get(DocumentStatus[this.data.status]).colorClass;
  }

  getPackageIconTooltip(status) {
    return DocumentStatusMap.get(DocumentStatus[this.data.status]).tooltip;
  }

  isDownloadAllAvailable(i) {
    let reciever = this.pack.sequenceDetails[i];
    let isDownloadable = true;
    reciever.documentDetails.forEach(detail => {
      if (!this.isDownloadable(detail.status)) {
        isDownloadable = false;
      }
    })

    if(reciever.cmrId !== this.currentCmrId){
      if(this.data.personalAcc){
        isDownloadable = false;
      }
    }
    return isDownloadable;
  }

  onDownloadAllDocuments(i) {
    const zip = new JSZip();
    const files = [];

    this.pack.sequenceDetails[i].documentDetails.forEach(doc => {
      files.push(this.signingService.getRequestedFile(doc.usageId));
    })

    forkJoin(files)
    .subscribe(
      res => {
        res.forEach(result => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {            let fileName = result.headers.get('Content-Disposition') || 'unknown';
            fileName = fileName.substring(fileName.indexOf('=') + 2, fileName.length - 1);
            fileName = decodeURIComponent(fileName);
            zip.file(fileName, result.body);
          }
        });

        zip.generateAsync({ type: 'blob' }).then(blob => {
          saveAs(blob, this.pack.name + '.zip');
        });
      }
    );
  }

  isIE() {
    const match = navigator.userAgent.search(/(?:Edge|MSIE|Trident\/.*; rv:)/);
    let isIE = false;
    if (match !== -1) {
      isIE = true;
    }
    return isIE;
  }

  onDownloadRegisteredMailReceipt(rowData) {
    const messageIdentifier = rowData.messageIdentifier;

    this.businessDownloadService.requestForReceiptData(messageIdentifier)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.businessDownloadService.downloadReceipt(res.data.pdfData, this.renderer);
        },
        error: (err) => {
          this.info.error(err.message);
        }
      })
  }

  onDownloadFile(fileId: any) {
    this.signingService.getRequestedFile(fileId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  isArchived() {
    return this.data.status == 'ARCHIVED';
  }


  onDownloadReport(usageId: string, reportType: string) {
    this.signingService.getReport(usageId, reportType)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result) => {
          if (result.status !== 200) {
            this.info.error(result.message);
          } else {
            this.signedfileName = result.headers.get('Content-Disposition');
            let fileName = this.signedfileName.substring(this.signedfileName.indexOf('=') + 2, (this.signedfileName.length - 1));
            if (this.isIE()) {
              fileName = decodeURIComponent(fileName);
              const nav = (window.navigator as any)
              if (nav.msSaveOrOpenBlob) {
                nav.msSaveOrOpenBlob(result.body, fileName);
              }
            } else {
              const url = window.URL.createObjectURL(result.body);
              const link = document.createElement('a');
              link.href = url;
              link.download = decodeURI(fileName);
              document.body.appendChild(link);
              link.click();
            }
          }
        }
      );
  }

  getReceiverName(receiver) {
    if (this.names.length) {
      let person = this.names.find((obj) => obj.id === receiver.cmrId);
      let nameBg = this.formatName(person.nameBg);
      let nameEn = this.formatName(person.nameEn);
      return this.translateService.currentLang == 'bg' ? nameBg : nameEn;
    }
  }

  formatName(fullName) {
    // Split the name by spaces and filter out any empty parts
    let nameParts = fullName.split(' ').filter(part => part.trim() !== '');

    // Join the first and last name (first and last element of the array)
    if (nameParts.length > 1) {
      return `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
    } else {
      return fullName;  // If only one name is found, return the original name
    }
  }

  isRegisteredDelivery(){
   return this.data.typeId === 240;
  }

  isBusinessProfile(){
    return !this.data.personalAcc;
  }
}


